/**
 * indicators.js — pure client-side technical indicator math over daily
 * OHLC bars ({ time, open, high, low, close, volume }). No backend calls;
 * computed on whatever window the chart already fetched.
 */

const round = (v, dp = 4) => Math.round(v * 10 ** dp) / 10 ** dp;

/** Simple Moving Average — overlay on the price pane. */
export function sma(bars, period = 20) {
    if (!bars || bars.length < period) return [];
    const out = [];
    let sum = 0;
    for (let i = 0; i < bars.length; i++) {
        sum += bars[i].close;
        if (i >= period) sum -= bars[i - period].close;
        if (i >= period - 1) out.push({ time: bars[i].time, value: round(sum / period) });
    }
    return out;
}

/** Exponential Moving Average over raw bars (close price) — overlay. */
export function ema(bars, period = 20) {
    return emaOfValues(bars.map((b) => ({ time: b.time, value: b.close })), period);
}

/** Generic EMA over an arbitrary {time,value} series (used to build MACD's signal line). */
export function emaOfValues(points, period) {
    if (!points || points.length < period) return [];
    const k = 2 / (period + 1);
    const out = [];
    let prev = points.slice(0, period).reduce((s, p) => s + p.value, 0) / period;
    out.push({ time: points[period - 1].time, value: round(prev) });
    for (let i = period; i < points.length; i++) {
        prev = points[i].value * k + prev * (1 - k);
        out.push({ time: points[i].time, value: round(prev) });
    }
    return out;
}

/** Wilder's RSI — needs its own 0-100 sub-pane below the price chart. */
export function rsi(bars, period = 14) {
    if (!bars || bars.length < period + 1) return [];
    const rsiFrom = (avgGain, avgLoss) =>
        avgLoss === 0 ? 100 : round(100 - 100 / (1 + avgGain / avgLoss), 2);
    let avgGain = 0, avgLoss = 0;
    for (let i = 1; i <= period; i++) {
        const diff = bars[i].close - bars[i - 1].close;
        avgGain += Math.max(diff, 0);
        avgLoss += Math.max(-diff, 0);
    }
    avgGain /= period; avgLoss /= period;
    const out = [{ time: bars[period].time, value: rsiFrom(avgGain, avgLoss) }];
    for (let i = period + 1; i < bars.length; i++) {
        const diff = bars[i].close - bars[i - 1].close;
        avgGain = (avgGain * (period - 1) + Math.max(diff, 0)) / period;
        avgLoss = (avgLoss * (period - 1) + Math.max(-diff, 0)) / period;
        out.push({ time: bars[i].time, value: rsiFrom(avgGain, avgLoss) });
    }
    return out;
}

/** MACD line + signal line + histogram — its own sub-pane below the price chart. */
export function macd(bars, fast = 12, slow = 26, signal = 9) {
    const emaFast = ema(bars, fast);
    const emaSlow = ema(bars, slow);
    const slowMap = new Map(emaSlow.map((p) => [p.time, p.value]));
    const macdLine = emaFast
        .filter((p) => slowMap.has(p.time))
        .map((p) => ({ time: p.time, value: round(p.value - slowMap.get(p.time)) }));
    const signalLine = emaOfValues(macdLine, signal);
    const signalMap = new Map(signalLine.map((p) => [p.time, p.value]));
    const histogram = macdLine
        .filter((p) => signalMap.has(p.time))
        .map((p) => {
            const diff = round(p.value - signalMap.get(p.time));
            return { time: p.time, value: diff, color: diff >= 0 ? "#22c55e" : "#ef4444" };
        });
    return { macdLine, signalLine, histogram };
}

/** Bollinger Bands — upper/mid/lower overlay on the price pane. */
export function bollinger(bars, period = 20, stdDev = 2) {
    const upper = [], mid = [], lower = [];
    if (!bars || bars.length < period) return { upper, mid, lower };
    for (let i = period - 1; i < bars.length; i++) {
        const slice = bars.slice(i - period + 1, i + 1);
        const mean = slice.reduce((s, b) => s + b.close, 0) / period;
        const variance = slice.reduce((s, b) => s + (b.close - mean) ** 2, 0) / period;
        const sd = Math.sqrt(variance);
        const t = bars[i].time;
        mid.push({ time: t, value: round(mean) });
        upper.push({ time: t, value: round(mean + stdDev * sd) });
        lower.push({ time: t, value: round(mean - stdDev * sd) });
    }
    return { upper, mid, lower };
}
